import React, { useCallback, useMemo, useState } from 'react';
import { reportsAPI } from '../../services/api';
import useReport from './shared/useReport';
import ReportTable from './shared/ReportTable';
import ReportPrintLayout from './shared/ReportPrintLayout';
import PrintButton from './shared/PrintButton';
import { fmtMoney, fmtNum } from './shared/chartTheme';
import useDrill from './shared/useDrill';

// Which batches are running out of time, straight from the receipt ledger - batch by batch, not
// medicine by medicine, because two batches of the same medicine can be a year apart. The bucket
// cards double as filters: click one to see only its batches.

const BUCKETS = [
  { key: 'EXPIRED', label: 'Expired', card: 'border-rose-300 bg-rose-50', text: 'text-rose-700' },
  { key: 'CRITICAL', label: 'Within 30 days', card: 'border-amber-300 bg-amber-50', text: 'text-amber-700' },
  { key: 'WARNING', label: '31-90 days', card: 'border-yellow-200 bg-yellow-50', text: 'text-yellow-700' },
  { key: 'OK', label: 'Over 90 days', card: 'border-emerald-200 bg-emerald-50', text: 'text-emerald-700' },
];

const BADGE = {
  EXPIRED: 'bg-rose-100 text-rose-700',
  CRITICAL: 'bg-amber-100 text-amber-700',
  WARNING: 'bg-yellow-100 text-yellow-700',
  OK: 'bg-emerald-100 text-emerald-700',
};

const COLUMNS = [
  { key: 'medicine', label: 'Medicine' },
  { key: 'genericName', label: 'Generic' },
  { key: 'batch', label: 'Batch' },
  { key: 'expiryDate', label: 'Expiry' },
  { key: 'daysToExpiry', label: 'Days', align: 'right', render: (r) => (Number(r.daysToExpiry) < 0 ? `${-r.daysToExpiry} ago` : r.daysToExpiry), csv: (r) => r.daysToExpiry },
  { key: 'remaining', label: 'Qty Left', align: 'right' },
  { key: 'value', label: 'Value', align: 'right', render: (r) => fmtMoney(r.value) },
  { key: 'vendor', label: 'Vendor' },
  { key: 'receivedOn', label: 'Received' },
  {
    key: 'bucket', label: 'Status',
    render: (r) => (
      <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${BADGE[r.bucket] || ''}`}>
        {BUCKETS.find((b) => b.key === r.bucket)?.label || r.bucket}
      </span>
    ),
    csv: (r) => BUCKETS.find((b) => b.key === r.bucket)?.label || r.bucket,
  },
];

const ExpiryWatchReport = () => {
  const drill = useDrill();
  const fetcher = useCallback(() => reportsAPI.getExpiryWatch(), []);
  const { data, loading } = useReport(fetcher);
  const [filter, setFilter] = useState(null);

  const summary = useMemo(() => {
    const rows = data?.rows || [];
    const out = {};
    BUCKETS.forEach((b) => {
      const mine = rows.filter((r) => r.bucket === b.key);
      out[b.key] = { count: mine.length, value: mine.reduce((s, r) => s + Number(r.value), 0) };
    });
    return out;
  }, [data]);

  const visible = useMemo(() => {
    const rows = data?.rows || [];
    return filter ? rows.filter((r) => r.bucket === filter) : rows;
  }, [data, filter]);

  return (
    <div className="space-y-5">
      <div className="flex items-center">
        <p className="text-sm text-gray-500">
          As of {data?.asOf || 'today'} · batches with stock remaining and a recorded expiry.
        </p>
        <div className="ml-auto"><PrintButton /></div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {BUCKETS.map((b) => (
          <button
            key={b.key}
            onClick={() => setFilter(filter === b.key ? null : b.key)}
            className={`rounded-2xl border p-4 text-left transition-all ${b.card} ${
              filter === b.key ? 'ring-2 ring-emerald-500' : 'hover:shadow'
            }`}
            title={filter === b.key ? 'Click to show all batches' : 'Click to show only these batches'}
          >
            <p className={`text-xs font-semibold uppercase tracking-wide ${b.text}`}>{b.label}</p>
            {loading ? (
              <div className="mt-2 h-7 w-16 bg-white/60 rounded animate-pulse" />
            ) : (
              <>
                <p className="mt-1 text-xl font-bold text-gray-900">{fmtNum(summary[b.key]?.count ?? 0)} batches</p>
                <p className="text-[11px] text-gray-600">{fmtMoney(summary[b.key]?.value ?? 0)} at purchase</p>
              </>
            )}
          </button>
        ))}
      </div>

      <ReportTable
        onRowClick={(r) => drill.open({ type: 'stockMedicine', value: r.medicine })}
        title={filter ? `Batches: ${BUCKETS.find((b) => b.key === filter)?.label}` : 'All batches by expiry'}
        columns={COLUMNS}
        rows={visible}
        csvName={`expiry-watch-${data?.asOf || ''}`}
        emptyText={filter ? 'No batches in this bucket' : 'No batches with stock and an expiry date'}
      />

      <ReportPrintLayout
        title="Expiry Watch"
        subtitle={`As of ${data?.asOf || ''}`}
        kpis={BUCKETS.map((b) => ({ label: b.label, value: `${summary[b.key]?.count ?? 0} · ${fmtMoney(summary[b.key]?.value ?? 0)}` }))}
        tables={[{ title: 'Batches by expiry', columns: COLUMNS, rows: data?.rows || [] }]}
      />

      {drill.node}
    </div>
  );
};

export default ExpiryWatchReport;
